/**
 * sl-uq2j.2.2: Workspace Chat Sidebar Tab — Persistent at Workbench Level.
 *
 * Architecture:
 * - WorkspaceChatSidebarTab: Rendered in workbench-root.tsx (NEVER unmounts)
 *   → Registers the chat ViewSidebarTab, creates ChatPanel singleton, sets all props
 * - DetailPageChatSync: Rendered in detail-page.tsx (syncs editor host/doc to cached panel)
 * - GraphExplorerChatSync: Rendered in graph-explorer (sets source click handler)
 *
 * The ChatPanel Lit element is cached as a workspace singleton (chatPanelCache).
 * Page components only UPDATE properties on the cached panel — they don't
 * register/unregister the sidebar tab, which eliminates flicker on navigation.
 */

import { notify, RevealButton, useConfirmModal } from '@affine/component';
import type { ChatPanel } from '@affine/core/blocksuite/ai';
import { AIProvider } from '@affine/core/blocksuite/ai';
import type { BotOptionData } from '@affine/core/blocksuite/ai/chat-panel/ai-title';
import {
  AI_SOURCE_MISSING_WARNING,
  openAiSourceNavigationDetail,
} from '@affine/core/blocksuite/ai/components/ai-tools/ai-source-navigation-handler';
import type { AiSourceNavigationDetail } from '@affine/core/blocksuite/ai/components/ai-tools/doc-search-source';
import { registerAIEffects } from '@affine/core/blocksuite/ai/effects';
import { createDetachedChatStd } from '@affine/core/blocksuite/ai/utils/detached-chat-std';
import type { AffineEditorContainer } from '@affine/core/blocksuite/block-suite-editor';
import { NotificationServiceImpl } from '@affine/core/blocksuite/view-extensions/editor-view/notification-service';
import { PurchaseConfirmDialog } from '@affine/core/components/consent';
import { useAIChatConfig } from '@affine/core/components/hooks/affine/use-ai-chat-config';
import { useAISpecs } from '@affine/core/components/hooks/affine/use-ai-specs';
import { useAISubscribe } from '@affine/core/components/hooks/affine/use-ai-subscribe';
import {
  AIDraftService,
  AIToolsConfigService,
} from '@affine/core/modules/ai-button';
import { AIModelService } from '@affine/core/modules/ai-button/services/models';
import { ServerService, SubscriptionService } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { DocsService } from '@affine/core/modules/doc';
import { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { PeekViewService } from '@affine/core/modules/peek-view';
import {
  FEATURE_KEY_AI_TARGET_MODE,
  useFeatureGate,
} from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import {
  getActiveWorkflowChatContext,
  WORKFLOW_CHAT_CONTEXT_EVENT,
  type WorkflowChatContextEventDetail,
} from '@affine/core/modules/streamlain/workflow-viewer/workflow-chat-context';
import { AppThemeService } from '@affine/core/modules/theme';
import {
  ViewService,
  ViewSidebarTab,
  WorkbenchService,
} from '@affine/core/modules/workbench';
import {
  getMobileChatFullWidth,
  getMobileChatNormalWidth,
  isMobileChatSidebarFullWidth,
  isMobileChatSidebarViewport,
} from '@affine/core/modules/workbench/mobile-chat-sidebar';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { RefNodeSlotsProvider } from '@blocksuite/affine/inlines/reference';
import { DocModeProvider } from '@blocksuite/affine/shared/services';
import { createSignalFromObservable } from '@blocksuite/affine/shared/utils';
import { BlockStdScope } from '@blocksuite/affine/std';
import { AiIcon } from '@blocksuite/icons/rc';
import { useFramework, useLiveData, useService } from '@toeverything/infra';
import { nanoid } from 'nanoid';
import {
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import * as chatSidebarStyles from './workspace-chat-sidebar-tab.css';

registerAIEffects();

// =============================================================================
// ChatPanel Singleton Cache
// =============================================================================

const chatPanelCache = new Map<string, ChatPanel>();
// sl-uq2j: Store mock BlockStdScope per workspace so DetailPageChatSync
// can restore it when navigating away from a doc page.
const chatPanelMockStdCache = new Map<string, BlockStdScope | null>();
// Tracks whether a REAL editor currently owns panel.host/panel.doc. The mock
// effect must never clobber the real editor context — losing the real doc id
// made AI canvas tools target the wrong (or no) document.
const chatPanelRealEditorCache = new Map<string, boolean>();

export function getCachedPanel(workspaceId: string): ChatPanel {
  let panel = chatPanelCache.get(workspaceId);
  if (!panel) {
    panel = document.createElement('chat-panel');
    chatPanelCache.set(workspaceId, panel);
  }
  return panel;
}

function getCachedMockStd(workspaceId: string): BlockStdScope | null {
  return chatPanelMockStdCache.get(workspaceId) ?? null;
}

/**
 * Apply the mock context for non-doc pages. The mock std exists only for
 * richer rendering — its (arbitrary) doc must NEVER leak as the chat's
 * active doc, so panel.doc stays undefined in mock mode.
 */
function applyMockPanelContext(panel: ChatPanel, workspaceId: string): void {
  const mockStd = getCachedMockStd(workspaceId);
  panel.host = mockStd?.host;
  panel.doc = undefined;
}

// =============================================================================
// WorkspaceChatSidebarTab — Rendered at WORKBENCH level (never unmounts)
// =============================================================================

const panelHostStyle = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
  width: '100%',
} as const;

const AIUpgradePrompt = () => {
  const t = useI18n();
  const { getRequiredPlan } = useFeatureGate();
  const workspaceDialogService = useService(WorkspaceDialogService);
  const requiredPlan = getRequiredPlan('managed-ai-credits') ?? 'Pro';
  const handleUpgrade = useCallback(() => {
    workspaceDialogService.open('setting', { activeTab: 'workspace:plans' });
  }, [workspaceDialogService]);

  return (
    <ViewSidebarTab
      tabId="chat"
      icon={<AiIcon />}
      label={t['com.streamlain.rightSidebar.ai-chat']()}
      unmountOnInactive={false}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          padding: '24px',
          gap: '16px',
          textAlign: 'center',
        }}
      >
        <AiIcon style={{ fontSize: 48, opacity: 0.4 }} />
        <div style={{ fontSize: 16, fontWeight: 600 }}>
          {t['com.streamlain.ai-gate.title']({ plan: requiredPlan })}
        </div>
        <div style={{ fontSize: 13, opacity: 0.6 }}>
          {t['com.streamlain.ai-gate.description']()}
        </div>
        <RevealButton
          label={t['com.streamlain.ai-gate.view-plans']()}
          onClick={handleUpgrade}
          wrapperStyle={{ width: 180, borderRadius: 8, overflow: 'hidden' }}
          data-testid="ai-gate-view-plans"
        />
      </div>
    </ViewSidebarTab>
  );
};

export const WorkspaceChatSidebarTab = () => {
  const { isFeatureAllowed } = useFeatureGate();
  const hasManagedAI = isFeatureAllowed('managed-ai-credits');

  if (!hasManagedAI) {
    return <AIUpgradePrompt />;
  }

  return <WorkspaceChatSidebarTabInner />;
};

const WorkspaceChatSidebarTabInner = () => {
  const t = useI18n();
  const { isFeatureAllowed } = useFeatureGate();
  const hasExpertBots = isFeatureAllowed('ai-expert-bots');
  const hasTargetMode = isFeatureAllowed(FEATURE_KEY_AI_TARGET_MODE);
  const chatRootRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const framework = useFramework();
  const workbench = useService(WorkbenchService).workbench;
  const docsService = useService(DocsService);
  const view = useService(ViewService).view;
  const workspace = useService(WorkspaceService).workspace;
  const sidebarOpened = useLiveData(workbench.sidebarOpen$);
  const sidebarWidth = useLiveData(workbench.sidebarWidth$);
  const activeSidebarTab = useLiveData(view.activeSidebarTab$);
  const [mobileChatViewport, setMobileChatViewport] = useState(() =>
    isMobileChatSidebarViewport()
  );
  const [chatViewportOffsets, setChatViewportOffsets] = useState({
    top: 0,
    bottom: 0,
  });

  const {
    docDisplayConfig,
    searchMenuConfig,
    networkSearchConfig,
    reasoningConfig,
    playgroundConfig,
  } = useAIChatConfig();
  const confirmModal = useConfirmModal();
  const specs = useAISpecs();
  const {
    openConsentDialog: handleAISubscribe,
    consentDialogProps: aiConsentProps,
  } = useAISubscribe();

  // Rendering-only host for non-doc pages. Never backed by a workspace doc.
  const detachedChatStd = useMemo(() => {
    // Keep the detached host scoped to the current workspace lifecycle.
    void workspace.id;
    return createDetachedChatStd();
  }, [workspace.id]);
  const mockStd = detachedChatStd.std;

  const panel = useMemo(() => getCachedPanel(workspace.id), [workspace.id]);

  useEffect(() => {
    const handleWorkflowContext = (event: Event) => {
      const detail = (event as CustomEvent<WorkflowChatContextEventDetail>)
        .detail;
      if (!detail || detail.workspaceId !== workspace.id) return;

      if (detail.clearContextKey) {
        if (
          panel.activeWorkflowAttachment?.contextKey === detail.clearContextKey
        ) {
          panel.activeWorkflowAttachment = null;
        }
        return;
      }
      if (!detail.attachment) return;

      panel.activeWorkflowAttachment = detail.attachment;
      if (detail.force) {
        panel.activeWorkflowContextForce += 1;
      }
      if (detail.openChat) {
        workbench.openSidebar();
        view.activeSidebarTab('chat');
      }
    };

    window.addEventListener(WORKFLOW_CHAT_CONTEXT_EVENT, handleWorkflowContext);
    const activeContext = getActiveWorkflowChatContext(workspace.id);
    if (activeContext) {
      handleWorkflowContext(
        new CustomEvent<WorkflowChatContextEventDetail>(
          WORKFLOW_CHAT_CONTEXT_EVENT,
          { detail: activeContext }
        )
      );
    }
    return () => {
      window.removeEventListener(
        WORKFLOW_CHAT_CONTEXT_EVENT,
        handleWorkflowContext
      );
    };
  }, [panel, view, workbench, workspace.id]);

  useEffect(() => {
    const subscription =
      AIProvider.slots.requestOpenCanvasChatSession.subscribe(request => {
        if (request.workspaceId !== workspace.id) return;
        workbench.openSidebar();
        view.activeSidebarTab('chat');
        panel.openCanvasSession(request.sessionId).then(opened => {
          if (!opened) {
            notify.error({
              title:
                t['com.streamlain.ai.chat.canvas.open-in-sidebar-failed'](),
            });
          }
        }, console.error);
      });
    return () => subscription.unsubscribe();
  }, [panel, t, view, workbench, workspace.id]);

  useEffect(() => {
    const root = chatRootRef.current;
    if (!root) return;

    let frame = 0;
    const updateOffsets = () => {
      frame = 0;
      const rect = root.getBoundingClientRect();
      const next = {
        top: Math.max(0, Math.round(rect.top)),
        bottom: Math.max(0, Math.round(window.innerHeight - rect.bottom)),
      };
      setChatViewportOffsets(current =>
        current.top === next.top && current.bottom === next.bottom
          ? current
          : next
      );
    };
    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(updateOffsets);
    };
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(root);
    window.addEventListener('resize', scheduleUpdate);
    scheduleUpdate();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [sidebarWidth, mobileChatViewport, activeSidebarTab?.id]);

  // sl-uq2j: Set mock host once on mount — DetailPageChatSync overrides with real editor.
  // Guarded: when a real editor already owns the panel context (doc page),
  // re-runs of this effect must not clobber it with the mock.
  useEffect(() => {
    chatPanelMockStdCache.set(workspace.id, mockStd ?? null);
    if (!chatPanelRealEditorCache.get(workspace.id)) {
      applyMockPanelContext(panel, workspace.id);
    }
  }, [panel, mockStd, workspace.id]);

  useEffect(
    () => () => {
      if (chatPanelMockStdCache.get(workspace.id) === mockStd) {
        chatPanelMockStdCache.delete(workspace.id);
      }
      detachedChatStd.dispose();
    },
    [detachedChatStd, mockStd, workspace.id]
  );

  // Set ALL other properties then append. Does NOT touch host/doc.
  useEffect(() => {
    panel.workspaceId = workspace.id;
    panel.targetModeEnabled = hasTargetMode;

    panel.appSidebarConfig = {
      getWidth: () => createSignalFromObservable(workbench.sidebarWidth$, 0),
      isOpen: () => createSignalFromObservable(workbench.sidebarOpen$, true),
    };

    panel.docDisplayConfig = docDisplayConfig;
    panel.searchMenuConfig = searchMenuConfig;
    panel.networkSearchConfig = networkSearchConfig;
    panel.reasoningConfig = reasoningConfig;
    panel.playgroundConfig = playgroundConfig;
    panel.extensions = specs;
    panel.serverService = framework.get(ServerService);
    panel.affineFeatureFlagService = framework.get(FeatureFlagService);
    panel.affineWorkspaceDialogService = framework.get(WorkspaceDialogService);
    panel.affineWorkbenchService = framework.get(WorkbenchService);
    panel.affineThemeService = framework.get(AppThemeService);
    panel.peekViewService = framework.get(PeekViewService);
    panel.notificationService = new NotificationServiceImpl(
      confirmModal.closeConfirmModal,
      confirmModal.openConfirmModal
    );
    panel.aiDraftService = framework.get(AIDraftService);
    panel.aiToolsConfigService = framework.get(AIToolsConfigService);
    panel.subscriptionService = framework.get(SubscriptionService);
    panel.workspaceDocSyncEngine = workspace.engine.doc;

    const aiModel = framework.get(AIModelService);
    aiModel.loadForWorkspace(workspace.id).catch(console.error);
    panel.aiModelService = aiModel;
    panel.onAISubscribe = handleAISubscribe;

    // LAST: Append to DOM (triggers connectedCallback — all props set above)
    if (containerRef.current && !containerRef.current.contains(panel)) {
      containerRef.current.append(panel);
    }
  }, [
    panel,
    hasTargetMode,
    docDisplayConfig,
    searchMenuConfig,
    networkSearchConfig,
    reasoningConfig,
    playgroundConfig,
    framework,
    workbench,
    workspace.id,
    workspace.engine.doc,
    confirmModal,
    specs,
    handleAISubscribe,
  ]);

  // Handle ref-node clicks in mock mode
  useEffect(() => {
    if (!mockStd) return;
    const refNodeSlots = mockStd.getOptional(RefNodeSlotsProvider);
    if (!refNodeSlots) return;
    const sub = refNodeSlots.docLinkClicked.subscribe(event => {
      workbench.openDoc({
        docId: event.pageId,
        mode: event.params?.mode,
        blockIds: event.params?.blockIds,
        elementIds: event.params?.elementIds,
        refreshKey: nanoid(),
      });
    });
    return () => sub.unsubscribe();
  }, [workbench, mockStd]);

  // navigate-to-source — default: open doc in edgeless
  // (GraphExplorerChatSync overrides this via window event)
  useEffect(() => {
    const container = containerRef.current;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<AiSourceNavigationDetail>).detail;
      const minimizeMobileChatSidebar = () => {
        if (activeSidebarTab?.id === 'chat' && isMobileChatSidebarViewport()) {
          workbench.closeSidebar();
        }
      };
      const didNavigate = openAiSourceNavigationDetail(detail, {
        docsService,
        workbench,
        onMissingSource: () => {
          notify.warning(AI_SOURCE_MISSING_WARNING);
        },
      });
      if (didNavigate) {
        minimizeMobileChatSidebar();
      }
    };
    container?.addEventListener('navigate-to-source', handler);
    window.addEventListener('navigate-to-source', handler);
    return () => {
      container?.removeEventListener('navigate-to-source', handler);
      window.removeEventListener('navigate-to-source', handler);
    };
  }, [activeSidebarTab?.id, docsService, workbench]);

  // Bot Switcher state
  const [bots, setBots] = useState<BotOptionData[]>([
    {
      id: null,
      name: t['com.streamlain.ai.bots.personal-assistant'](),
      description: '',
    },
  ]);
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);
  const [isMemoryEnabled, setIsMemoryEnabled] = useState(false);

  useEffect(() => {
    const serverService = framework.get(ServerService);
    const server = serverService.server;
    const workspaceId = workspace.id;

    server
      .gql({
        query: {
          id: 'workspaceMemoryStatusQuery',
          query: `query workspaceMemoryStatus($id: String!) {
            workspace(id: $id) { enablePersonalMemory }
          }`,
          operationName: 'workspaceMemoryStatus',
          definitionName: 'workspace',
        } as any,
        variables: { id: workspaceId } as any,
      })
      .then((res: any) => {
        setIsMemoryEnabled(res?.workspace?.enablePersonalMemory ?? false);
      })
      .catch(() => setIsMemoryEnabled(false));

    if (!hasExpertBots) {
      setBots([
        {
          id: null,
          name: t['com.streamlain.ai.bots.personal-assistant'](),
          description: '',
        },
      ]);
      return;
    }

    server
      .gql({
        query: {
          id: 'workspaceBotsQuery',
          query: `query workspaceBots($workspaceId: String!) {
            workspaceBots(workspaceId: $workspaceId) { id name description }
          }`,
          operationName: 'workspaceBots',
          definitionName: 'workspaceBots',
        } as any,
        variables: { workspaceId } as any,
      })
      .then((res: any) => {
        const botList = res?.workspaceBots ?? [];
        const options: BotOptionData[] = [
          {
            id: null,
            name: t['com.streamlain.ai.bots.personal-assistant'](),
            description: '',
          },
          ...botList.map((b: any) => ({
            id: b.id,
            name: b.name,
            description: b.description ?? '',
          })),
        ];
        setBots(options);
      })
      .catch(() => {
        setBots([
          {
            id: null,
            name: t['com.streamlain.ai.bots.personal-assistant'](),
            description: '',
          },
        ]);
      });
  }, [framework, hasExpertBots, t, workspace.id]);

  const handleSelectBot = useCallback((botId: string | null) => {
    setSelectedBotId(botId);
    AIProvider.currentBotId = botId;
  }, []);

  useEffect(() => {
    panel.bots = bots;
    panel.selectedBotId = selectedBotId;
    panel.onSelectBot = handleSelectBot;
    panel.isMemoryEnabled = isMemoryEnabled;
  }, [panel, bots, selectedBotId, handleSelectBot, isMemoryEnabled]);

  // Auto-resize sidebar for preview panels
  const [autoResized, setAutoResized] = useState(false);
  useEffect(() => {
    if (autoResized) return;
    const subscription = AIProvider.slots.previewPanelOpenChange.subscribe(
      open => {
        if (!open) return;
        const sidebarWidth = workbench.sidebarWidth$.value;
        const MIN_SIDEBAR_WIDTH = 1080;
        if (!sidebarWidth || sidebarWidth < MIN_SIDEBAR_WIDTH) {
          workbench.setSidebarWidth(MIN_SIDEBAR_WIDTH);
          setAutoResized(true);
        }
      }
    );
    return () => subscription.unsubscribe();
  }, [autoResized, workbench]);

  useEffect(() => {
    const updateMobileChatViewport = () => {
      setMobileChatViewport(isMobileChatSidebarViewport());
    };
    updateMobileChatViewport();
    window.addEventListener('resize', updateMobileChatViewport);
    return () => {
      window.removeEventListener('resize', updateMobileChatViewport);
    };
  }, []);

  useEffect(() => {
    if (
      !mobileChatViewport ||
      !sidebarOpened ||
      activeSidebarTab?.id !== 'chat'
    ) {
      return;
    }
    const currentWidth = workbench.sidebarWidth$.value ?? 0;
    const fullWidth = getMobileChatFullWidth();
    if (currentWidth > fullWidth) {
      workbench.setSidebarWidth(fullWidth);
    }
  }, [activeSidebarTab?.id, mobileChatViewport, sidebarOpened, workbench]);

  const handleToggleMobileSidebarWidth = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isMobileChatSidebarViewport()) {
        return;
      }
      const nextWidth = isMobileChatSidebarFullWidth(
        workbench.sidebarWidth$.value
      )
        ? getMobileChatNormalWidth()
        : getMobileChatFullWidth();
      workbench.setSidebarWidth(nextWidth);
    },
    [workbench]
  );

  const chatRootStyle = {
    '--streamlain-chat-sidebar-width': `${
      sidebarWidth && sidebarWidth > 0 ? sidebarWidth : 454
    }px`,
    '--streamlain-chat-sidebar-top-offset': `${chatViewportOffsets.top}px`,
    '--streamlain-chat-sidebar-bottom-offset': `${chatViewportOffsets.bottom}px`,
  } as CSSProperties;

  return (
    <ViewSidebarTab
      tabId="chat"
      icon={<AiIcon />}
      label={t['com.streamlain.rightSidebar.ai-chat']()}
      unmountOnInactive={false}
    >
      <div
        ref={chatRootRef}
        className={chatSidebarStyles.chatRoot}
        style={chatRootStyle}
      >
        {mobileChatViewport ? (
          <button
            type="button"
            className={chatSidebarStyles.mobileWidthToggleHitArea}
            aria-label="Chatbreite umschalten"
            title="Chatbreite umschalten"
            onClick={handleToggleMobileSidebarWidth}
            data-full-width={isMobileChatSidebarFullWidth(sidebarWidth)}
          />
        ) : null}
        <div style={panelHostStyle} ref={containerRef} />
      </div>
      <PurchaseConfirmDialog {...aiConsentProps} />
    </ViewSidebarTab>
  );
};

// =============================================================================
// DetailPageChatSync — Syncs real editor host/doc to the cached ChatPanel
// Rendered inside detail-page.tsx (mounts/unmounts with doc navigation)
// =============================================================================

export const DetailPageChatSync = ({
  editor,
}: {
  editor: AffineEditorContainer | null;
}) => {
  const workspace = useService(WorkspaceService).workspace;
  const panel = useMemo(() => getCachedPanel(workspace.id), [workspace.id]);

  useEffect(() => {
    // sl-uq2j: Restore mock host helper (active doc cleared in mock mode)
    const restoreMock = () => {
      chatPanelRealEditorCache.set(workspace.id, false);
      applyMockPanelContext(panel, workspace.id);
    };

    if (!editor?.host) {
      // Leaving doc page — restore mock context
      return restoreMock;
    }
    // Override with real editor
    chatPanelRealEditorCache.set(workspace.id, true);
    panel.host = editor.host;
    panel.doc = editor.doc;

    const docModeService = editor.host.std.get(DocModeProvider);
    const refNodeService = editor.host.std.getOptional(RefNodeSlotsProvider);
    const disposable = [
      refNodeService?.docLinkClicked.subscribe(({ host }) => {
        if (host === editor.host) {
          panel.doc = editor.doc;
        }
      }),
      docModeService?.onPrimaryModeChange(() => {
        if (editor.host) {
          panel.host = editor.host;
        }
      }, editor.doc.id),
    ];
    return () => {
      disposable.forEach(d => d?.unsubscribe());
      // Restore mock when leaving doc page
      restoreMock();
    };
  }, [editor, panel, workspace.id]);

  return null; // No UI — just syncs props
};

// =============================================================================
// GraphExplorerChatSync — Sets graph-specific source click handler
// Rendered inside graph-explorer/index.tsx
// =============================================================================

export const GraphExplorerChatSync = () => {
  // Graph Explorer overrides navigate-to-source via window event
  // The default handler in WorkspaceChatSidebarTab opens docs in edgeless,
  // but Graph Explorer wants to dispatch graph-select-by-doc instead.
  // This is handled via the global navigate-to-source event + pathname check
  // already in the EditorChatPanel/chat.tsx pattern.
  // For now, no additional sync needed — the navigate-to-source handler
  // in WorkspaceChatSidebarTab defaults to openDoc which is correct.
  return null;
};
